// OPS-SIGNAL — client-side ops-signal reporter
// (docs/audit/fastfood-workflow/IMPLEMENTATION_ACTION_PLAN.md Phase 3 item 4).
//
// The browser observes two operational facts the server cannot: a dropped
// realtime channel and an abandoned counter ticket. This posts them to
// /api/observability/op-signal, which lands them in continuity_events under the
// ops:* family beside the server-emitted ops events. Same contract as
// reportClientError (lib/client/observability/client-error.ts): a pure builder
// is exported for testing, the fetch is fire-and-forget and never throws, so a
// failed report can never break the code path that triggered it.

import {
  MAX_ROUTE_LENGTH,
  OP_SIGNAL_ID,
  type OpSignal,
} from '@/lib/server/observability/op-signal-schema';

export type OpSignalReport = {
  signal: OpSignal;
  /** Route path the signal happened on. Defaults to window.location.pathname. */
  route?: string;
  branchId?: string;
  correlationId?: string;
  /** Cart abandonment only: units on the ticket when it was dropped. */
  itemCount?: number;
  /** Source time of the event; defaults to now server-side. */
  happenedAt?: string;
};

export function buildOpSignalPayload(report: OpSignalReport): Record<string, unknown> {
  const route =
    report.route ?? (typeof window !== 'undefined' ? window.location.pathname : undefined);

  const payload: Record<string, unknown> = {
    signal: report.signal,
    route: (route || '/').slice(0, MAX_ROUTE_LENGTH),
  };

  if (report.correlationId && OP_SIGNAL_ID.test(report.correlationId)) {
    payload.correlation_id = report.correlationId;
  }
  if (report.branchId) {
    payload.branch_id = report.branchId;
  }
  if (typeof report.itemCount === 'number' && Number.isFinite(report.itemCount)) {
    payload.item_count = Math.max(0, Math.round(report.itemCount));
  }
  if (report.happenedAt) {
    payload.happened_at = report.happenedAt;
  }

  return payload;
}

export async function reportOpSignal(report: OpSignalReport): Promise<void> {
  try {
    // No window ⇒ server render / test without a DOM: there is no client signal
    // to send, so do nothing rather than attempt a relative fetch.
    if (typeof window === 'undefined') return;
    const payload = buildOpSignalPayload(report);
    await fetch('/api/observability/op-signal', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Fire-and-forget: telemetry must never surface in the caller.
  }
}
